import { type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RelatedPagesGrid, type RelatedPageCard } from "@/components/RelatedPagesGrid";
import { ComparisonTable, type ComparisonRow } from "@/components/geo/ComparisonTable";
import { describeRoute } from "@/lib/spokeRelatedDescriptions";
import {
  SCHEMA_LOCALBUSINESS_ID,
  SCHEMA_ORG_ID,
  SCHEMA_PERSON_ID,
  SITE_URL,
  buildLocalBusinessSchema,
} from "@/lib/entitySchema";
import { getPageLastUpdated } from "@/lib/lastUpdatedRegistry";
import { geoImageObject } from "@/lib/geoImageSchema";
import { ogImageFor, ogMetaTags } from "@/lib/ogImages";
import { LastUpdated } from "@/components/LastUpdated";
import { StatewideCoverageStrip } from "@/components/StatewideCoverageStrip";
import { PricingTeaserStrip } from "@/components/PricingTeaserStrip";
import { FreeAuditCTABanner } from "@/components/FreeAuditCTABanner";
import { MeetChrisStrip } from "@/components/MeetChrisStrip";
import { GenericCaseStudyStrip } from "@/components/GenericCaseStudyStrip";
import { IndustryAdviceSection } from "@/components/IndustryAdviceSection";

export type SpokeFAQ = { q: string; a: string };

export type SpokeRelatedLink = {
  /** TanStack route path. Caller is responsible for it being a valid registered route. */
  to: string;
  label: string;
  /** Optional one-line description for the card grid. Falls back to label. */
  description?: string;
};

export type SpokePageConfig = {
  /** URL slug WITHOUT leading slash, e.g. "colorado-springs-google-maps". */
  slug: string;
  /** Crumbs above hero (excluding the final "current page" crumb). */
  breadcrumbs: { label: string; to?: string }[];
  /** Final breadcrumb label (current page). */
  currentLabel: string;
  /** Hero eyebrow chip. */
  eyebrow: string;
  /** H1 — primary keyword phrase. */
  h1: ReactNode;
  /** Hero subheading paragraph. */
  heroDescription: string;
  /** Speakable opening paragraph (3 sentences max — gets pulled by AI). */
  intro: string;
  /** Definition callout: { term, body } — styled as definition-box. */
  definition: { term: string; body: string };
  /** "What we do" — 3-6 short bullets. */
  whatWeDo: { title: string; body: string }[];
  /** Why this market section: heading + body paragraphs. */
  whyThisMarket: { heading: string; body: string }[];
  /** Related internal links to satisfy internal linking rules. */
  relatedLinks: SpokeRelatedLink[];
  /** FAQ — 4+ questions. */
  faqs: SpokeFAQ[];
  /** Service schema name (e.g. "Google Maps Optimization in Colorado Springs"). */
  serviceName: string;
  /** Service schema description. */
  serviceDescription: string;
  /** areaServed for Service schema. */
  areaServed: string;
  /** Meta title. */
  metaTitle: string;
  /** Meta description. */
  metaDescription: string;
  /**
   * Optional override for the rel=canonical and og:url. Use this when this
   * spoke's content overlaps a flagship hub (e.g. an industry "-seo-colorado"
   * spoke that overlaps the "-marketing-colorado" flagship) and you want to
   * consolidate ranking signal onto the flagship without 301-redirecting.
   * Provide an absolute URL (e.g. "https://coloradowebimpressions.com/hvac-marketing-colorado").
   * Schema @id values still use the spoke's own URL so the page stays uniquely identifiable.
   */
  canonicalOverride?: string;
  /** Optional comparison table rendered between "What we do" and "Why this market". */
  comparisonTable?: {
    caption?: string;
    headerA: string;
    headerB: string;
    rows: ComparisonRow[];
  };
  /** Optional ISO date override for the "Last updated" stamp + schema dateModified. */
  lastUpdated?: string;
  /** Optional SEO hero image rendered below the intro. Also sets og:image / twitter:image. */
  heroImage?: { src: string; alt: string; geo?: { lat: number; lng: number; city: string } };
  /** Optional background photo behind the top hero (dark overlay keeps text readable). */
  heroBackground?: { src: string; alt: string };
};

export function buildSpokeHead(config: SpokePageConfig) {
  const selfUrl = `${SITE_URL}/${config.slug}`;
  const canonical = config.canonicalOverride ?? selfUrl;
  const lastUpdated = config.lastUpdated ?? getPageLastUpdated(`/${config.slug}`);
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${selfUrl}#service`,
        name: config.serviceName,
        description: config.serviceDescription,
        provider: { "@id": SCHEMA_LOCALBUSINESS_ID },
        areaServed: { "@type": "Place", name: config.areaServed },
        url: canonical,
        offers: {
          "@type": "AggregateOffer",
          priceCurrency: "USD",
          lowPrice: "950",
          highPrice: "5950",
        },
        dateModified: lastUpdated,
      },
      buildLocalBusinessSchema(),
      {
        "@type": "BreadcrumbList",
        "@id": `${selfUrl}#breadcrumbs`,
        itemListElement: [
          ...config.breadcrumbs.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.label,
            item: c.to ? `${SITE_URL}${c.to}` : undefined,
          })),
          {
            "@type": "ListItem",
            position: config.breadcrumbs.length + 1,
            name: config.currentLabel,
            item: selfUrl,
          },
        ],
      },
      {
        "@type": "FAQPage",
        "@id": `${selfUrl}#faq`,
        mainEntity: config.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "WebPage",
        "@id": `${selfUrl}#webpage`,
        url: canonical,
        name: config.metaTitle,
        dateModified: lastUpdated,
        speakable: {
          "@type": "SpeakableSpecification",
          cssSelector: [".speakable-intro", ".faq-answer", ".definition-box"],
        },
      },
    ],
  };

  const ogImage = config.heroImage?.src
    ? (config.heroImage.src.startsWith("http") ? config.heroImage.src : `${SITE_URL}${config.heroImage.src}`)
    : `${SITE_URL}${ogImageFor({})}`;

  if (config.heroImage?.src) {
    (schema["@graph"] as unknown[]).push(
      geoImageObject({
        url: ogImage,
        caption: config.heroImage.alt,
        cityName: config.heroImage.geo?.city ?? "Colorado Springs",
        geo: config.heroImage.geo
          ? { lat: config.heroImage.geo.lat, lng: config.heroImage.geo.lng }
          : { lat: 38.9359976, lng: -104.7653118 },
        width: 1280,
        height: 720,
      }),
    );
  }

  return {
    meta: [
      { title: config.metaTitle },
      { name: "description", content: config.metaDescription },
      { property: "og:title", content: config.metaTitle },
      { property: "og:description", content: config.metaDescription },
      { property: "og:url", content: canonical },
      { property: "og:type", content: "website" },
      ...ogMetaTags(ogImage),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: config.metaTitle },
      { name: "twitter:description", content: config.metaDescription },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: canonical }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(schema) }],
  };
}

export function SpokePage({ config, extraSections }: { config: SpokePageConfig; extraSections?: ReactNode }) {
  return (
    <SiteLayout>
      <PageHero
        eyebrow={config.eyebrow}
        title={config.h1}
        description={config.heroDescription}
        image={config.heroBackground}
      >
        <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
          >
            Get a Free Proposal
            <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href="tel:+17194193935"
            className="inline-flex items-center gap-2 rounded-md border-2 border-brand-gold/40 bg-card/10 px-6 py-3 text-base font-bold text-brand-gold backdrop-blur transition-smooth hover:border-brand-gold hover:bg-card/20"
            aria-label="Call (719) 419-3935"
          >
            <Phone className="h-4 w-4" />
            (719) 419-3935
          </a>
        </div>
      </PageHero>

      {/* Breadcrumbs — moved below hero for cleaner above-the-fold */}
      <nav aria-label="Breadcrumb" className="border-b border-border bg-muted/30">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-5 py-4 lg:px-8">
          <Breadcrumbs
            items={[
              ...config.breadcrumbs,
              { label: config.currentLabel },
            ]}
          />
          <LastUpdated date={config.lastUpdated ?? getPageLastUpdated(`/${config.slug}`)} />
        </div>
      </nav>

      {/* Optional SEO hero image */}
      {config.heroImage && (
        <section className="bg-background pt-10 lg:pt-14">
          <div className="mx-auto max-w-4xl px-5 lg:px-8">
            <img
              src={config.heroImage.src}
              alt={config.heroImage.alt}
              loading="eager"
              fetchPriority="high"
              width={1280}
              height={720}
              data-geo-lat={String(config.heroImage.geo?.lat ?? 38.9359976)}
              data-geo-lng={String(config.heroImage.geo?.lng ?? -104.7653118)}
              data-geo-city={config.heroImage.geo?.city ?? "Colorado Springs, CO"}
              className="aspect-[16/9] w-full rounded-2xl object-cover shadow-card"
            />
          </div>
        </section>
      )}

      {/* Intro + definition */}
      <section className="bg-background py-14 lg:py-16">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <p className="speakable-intro text-lg leading-relaxed text-foreground/85">
            {config.intro}
          </p>

          <div className="definition-box mt-8 rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-brand-gold-deep">
              What is {config.definition.term}?
            </div>
            <p className="mt-2 text-base leading-relaxed text-foreground/85">
              {config.definition.body}
            </p>
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="border-y border-border bg-section-alt py-16 lg:py-20">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            What we actually do
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {config.whatWeDo.map((item) => (
              <div
                key={item.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card"
              >
                <h3 className="text-lg font-extrabold text-foreground">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/85">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Optional comparison table */}
      {config.comparisonTable && (
        <section className="border-b border-border bg-background py-16 lg:py-20">
          <div className="mx-auto max-w-5xl px-5 lg:px-8">
            <ComparisonTable
              caption={config.comparisonTable.caption}
              headerA={config.comparisonTable.headerA}
              headerB={config.comparisonTable.headerB}
              rows={config.comparisonTable.rows}
            />
          </div>
        </section>
      )}

      {/* Why this market */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Why this market is different
          </h2>
          <div className="mt-8 space-y-8">
            {config.whyThisMarket.map((item) => (
              <div key={item.heading}>
                <h3 className="text-xl font-extrabold text-foreground">
                  {item.heading}
                </h3>
                <p className="mt-3 text-base leading-relaxed text-foreground/85">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {extraSections}
      <IndustryAdviceSection slug={config.slug} />

      {/* Related links — shared "More from Colorado Web Impressions" grid */}
      <RelatedPagesGrid
        cards={config.relatedLinks.map<RelatedPageCard>((link) => ({
          to: link.to,
          title: link.label,
          description:
            link.description ??
            describeRoute(link.to) ??
            `Explore ${link.label.toLowerCase()} from our Colorado team.`,
        }))}
      />

      {/* FAQ */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            FAQ
          </h2>
          <Accordion
            type="single"
            collapsible
            className="mt-8 divide-y divide-border rounded-2xl border border-border bg-card shadow-card"
          >
            {config.faqs.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                className="border-0 px-5 sm:px-6"
              >
                <AccordionTrigger className="py-5 text-left text-base font-bold text-foreground hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="faq-answer pb-5 pt-0 text-base leading-relaxed text-foreground/85">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      <GenericCaseStudyStrip />
      <PricingTeaserStrip />
      <FreeAuditCTABanner />
      <StatewideCoverageStrip />
      <MeetChrisStrip />

      {/* CTA */}
      <section className="bg-section-alt py-20">
        <div className="mx-auto max-w-3xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Ready for results in {config.areaServed}?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Talk directly with Chris — owner of Colorado Web Impressions. Real
            answers, real strategy, no sales pitch.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-6 py-3 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Get a Free Proposal
              <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border-2 border-foreground/20 bg-card px-6 py-3 text-base font-bold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
             aria-label="Call (719) 419-3935">
              <Phone className="h-4 w-4" />
              (719) 419-3935
            </a>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
